// =============================================================================
//  Copyright : 2018 semantics GmbH
//  Date      : $Date: 2025-02-24 21:31:32 +0100 (Mon, 24 Feb 2025) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 150530 $
//  Purpose   : wrap codemirror
// =============================================================================
// ---Third-party imports
import React, {Component} from 'react'
import PropTypes from 'prop-types'
import {Menu} from 'semantic-ui-react'
import clsx from 'clsx'

// ---Framework imports
import {isArray, isNil} from 'ldash'
import {CodeMirror, DiffView} from 'uix/chunks'
import {hashObj, MIME} from 'uix/utils'
import log from 'uix/logging'
import connect from 'uix/connect'
import {
  useRef,
  useEffect,
  useDimensions,
  useState,
  useSelector,
  usePersistentShallow,
} from 'uix/hooks'

// ---Local imports
import {default as AutoSizer} from './autosizer/AutoSizer'
import {AutoPortal} from './portals'
import {XMenu} from './menu'
import {Toolbar} from './toolbar'
import S from './editor.scss'

// ---Globals
const GUTTER_ID = 'CustomMarker'

const getCodemirrorTheme = theme => (theme == 'dark' ? 'lesser-dark' : 'default')

export const mimetype2options = mimetype => {
  let xml_opts = {
    autoCloseTags: true,
    xml: true,
    matchTags: {bothTags: true},
  }
  if (mimetype == MIME.MARKDOWN) {
    return {
      mode: {
        name: 'markdown',
        // allow lazy headers without whitespace between hashtag and text
        allowAtxHeaderWithoutSpace: true,
        // Whether to separately highlight markdown meta characterts (*[]()etc.)
        highlightFormatting: true,
      },
      options: xml_opts,
      extraKeys: {Enter: 'newlineAndIndentContinueMarkdownList'},
    }
  } else if (mimetype == MIME.HTML) {
    // better folding than markown/html-mixed
    return {
      mode: {name: 'text/html'},
      options: xml_opts,
    }
  } else if (
    mimetype == MIME.XML ||
    mimetype == MIME.XHTML ||
    mimetype == MIME.XSLT ||
    mimetype == MIME.HOCR ||
    mimetype == MIME.TEI ||
    mimetype?.endsWith('+xml')
  ) {
    return {
      mode: {name: 'xml'},
      options: xml_opts,
      extraKeys: {'Ctrl-J': 'toMatchingTag'},
    }
  } else if ([MIME.JS, MIME.JSON, MIME.JSONLD, MIME.CSS].includes(mimetype)) {
    // codemirror supports custom rules for json and jsonld mimetypes
    return {
      mode: {name: mimetype},
    }
  } else {
    log.warn('unknown mimetype: %o', mimetype)
  }
  return {mode: {name: 'plain'}}
}

const nullable = (props, key) => (props[key] === null ? null : 'Not null')
class _Editor extends Component {
  static storeMap = root => ({
    theme: getCodemirrorTheme(root.meta.theme.name),
  })

  static propTypes = {
    text: PropTypes.oneOfType([PropTypes.string.isRequired, nullable]),
    flex: PropTypes.bool, // build flex wrapper
    bordered: PropTypes.bool, // no border
    markers: PropTypes.array, // draw marker in gutter (if line given) or at charlevel (if line + column given)
  }

  static defaultProps = {
    mimetype: null,
    text: '',
    opts: {},
    flex: false,
    bordered: false,
  }

  constructor(props) {
    super(props)
    this.cm = null
    this.markers = []
    this.state = {
      ..._Editor.getDerivedStateFromProps(props, {}),
      cursor: {line: 0, ch: 0},
      scroll: {x: 0, y: 0},
    }
  }

  static getDerivedStateFromProps(props, state) {
    let nextState = {}
    if (state.textId !== hashObj(props.text)) {
      let {cursor, scroll} = props
      if (cursor) {
        nextState = {...nextState, cursor}
      }
      if (scroll) {
        nextState = {...nextState, scroll}
      }
      nextState = {
        ...nextState,
        textId: hashObj(props.text),
        // default to empty string on null/undefined
        currentText: props.text ?? '',
        changesPending: false,
      }
    }

    return nextState
  }

  updateMarkText = () => {
    let css = 'text-decoration: underline;text-decoration-style: wavy; text-decoration-color:red;'
    // clear all char level markers
    this.markers.forEach(marker => marker.clear())
    // clear all line level gutter markers
    this.cm.clearGutter(GUTTER_ID)

    let makeMarker = (sign, msg, color) => {
      let marker = document.createElement('div')
      marker.setAttribute('title', msg)
      marker.style.color = color
      marker.style['margin-left'] = '-5px'
      marker.style['width'] = '10px' // for better title-hovering
      marker.style['font-size'] = '1.5em' // for better title-hovering
      marker.innerHTML = sign
      return marker
    }
    if (this.props.markers) {
      for (let m of this.props.markers) {
        let {line, column, title, ...markerOpts} = m
        if (column && isArray(column)) {
          // mark a line from left_ch to right_ch
          let [left_ch, right_ch] = column
          this.markers.push(
            this.cm.markText(
              {line: line - 1, ch: left_ch},
              {line: line - 1, ch: right_ch},
              {css, title, ...markerOpts}
            )
          )
        } else {
          // mark line in gutter
          this.cm.setGutterMarker(line - 1, GUTTER_ID, makeMarker('&#9888;', title, '#F44'))
        }
      }
    }
  }
  componentDidUpdate(prevProps) {
    //
    let needUpdate = this.props.text !== prevProps.text || this.props.markers !== prevProps.markers
    if (needUpdate && this.cm) {
      this.updateMarkText()
      //~ this.cm.refresh()
    }
  }

  mountEditor = (editor, width, height) => {
    let {editorDidMount} = this.props
    let {scroll} = this.state

    editor.setSize(width, height)
    this.cm = editor
    this.updateMarkText()
    // after setSize
    this.cm.scrollTo(scroll.x, scroll.y)

    if (editorDidMount) {
      editorDidMount(editor)
    }
  }

  unmountEditor = () => {
    let {editorWillUnmount} = this.props
    this.cm = null
    if (editorWillUnmount) {
      editorWillUnmount()
    }
  }

  onBeforeChange = (editor, data, value) => {
    /* we receive new text from editor (due to editing) and store it
       in this.state.currentText
    */
    let {text, onBeforeChange} = this.props
    let changesPending = text !== value

    this.setState({currentText: value, changesPending, textId: hashObj(value)})
    if (onBeforeChange) {
      onBeforeChange({editor, data, value, changesPending})
    }
  }

  onCursor = (editor, data) => {
    // maybe save to ui store with a globally unique name?
    //if (this.cm) {
    //  this.setState({cursor: data})
    //}
    let {onCursor} = this.props
    if (onCursor) {
      onCursor({editor, data})
    }
  }

  onScroll = (editor, data) => {
    let {onScroll} = this.props

    if (onScroll) {
      onScroll({editor, data: {x: data.left, y: data.top}})
    }
  }

  onSelection = (editor, data) => {
    //~ log('sel %o', data)
    //
  }

  onContextMenu = (editor, event) => {
    if (!event.ctrlKey && this.props.onContextMenu) {
      this.props.onContextMenu({editor, event})
    }
  }

  render() {
    let {opts, extraKeys, flex, bordered, mimetype, theme} = this.props
    let {currentText, changesPending, cursor} = this.state

    let {mode, options: mimeOptions = {}, extraKeys: mimetypeExtraKeys = {}} = mimetype
      ? mimetype2options(mimetype)
      : {}

    extraKeys = {
      'Ctrl-Q': cm => cm.foldCode(cm.getCursor()),
      F11: cm => cm.setOption('fullScreen', !cm.getOption('fullScreen')),
      Esc: cm => (cm.getOption('fullScreen') ? cm.setOption('fullScreen', false) : null),
      // https://github.com/codemirror/CodeMirror/issues/988#issuecomment-392232020
      Tab: cm => cm.execCommand('indentMore'),
      'Shift-Tab': cm => cm.execCommand('indentLess'),
      ...mimetypeExtraKeys,
      ...extraKeys,
    }

    let codemirror = ({width, height}) => {
      if (bordered) {
        width -= 2
        height -= 2
      }
      if (this.cm && !this.cm.getOption('fullScreen')) {
        this.cm.setSize(width, height)
      }

      let options = {
        mode,
        theme,
        lineNumbers: true,
        lineWrapping: false,
        foldGutter: true,
        gutters: ['CodeMirror-linenumbers', 'CodeMirror-foldgutter', GUTTER_ID],
        keyMap: 'sublime',
        extraKeys,
        autoFocus: false,
        readOnly: false, //  If the special value "nocursor" is given (instead of simply true), focusing of the editor is also disallowed.
        ...mimeOptions,
        ...opts,
      }

      return (
        <CodeMirror
          value={currentText}
          cursor={cursor}
          autoScroll={false}
          editorDidMount={editor => this.mountEditor(editor, width, height)}
          editorWillUnmount={this.unmountEditor}
          options={options}
          selection={{
            ranges: [],
            focus: false, // defaults false if not specified
          }}
          onBeforeChange={this.onBeforeChange}
          onChange={(editor, data, value) => null}
          onCursor={this.onCursor}
          onScroll={this.onScroll}
          onSelection={this.onSelection}
          onContextMenu={this.onContextMenu}
        />
      )
    }

    let result = (
      <AutoSizer defaultWidth={500} defaultHeight={300}>
        {codemirror}
      </AutoSizer>
    )
    return <div className={clsx(flex && S.flex, bordered && S.bordered)}>{result}</div>
  }
}
export const Editor = connect(
  'Editor',
  _Editor
)

export const SyntaxHighlighter = ({
  text,
  bordered,
  opts,
  flex,
  disableHeight = false,
  mimetype = null,
  style,
}) => {
  let options = {
    readOnly: true,
    lineNumbers: false,
    lineWrapping: true,
    foldGutter: true,
    gutters: ['CodeMirror-foldgutter'],
  }
  let {mode = {name: 'xml'}, options: mimeOptions = {}} = mimetype ? mimetype2options(mimetype) : {}

  let [dimensions, ref] = useDimensions({delta: 50})

  options = {mode, ...options, ...opts, ...mimeOptions}
  let cmRef = useRef(null)
  let mountEditor = editor => (cmRef.current = editor)

  useEffect(() => {
    let {width, height} = dimensions
    if (bordered) {
      width -= 2
      height -= 2
    }

    if (cmRef.current) {
      cmRef.current.setSize(width, height)
    }
  }, [dimensions.width, dimensions.height, bordered])

  return (
    <div ref={ref} style={style} className={clsx(flex && S.flex, bordered && S.bordered)}>
      <CodeMirror value={text} options={options} editorDidMount={mountEditor} />
    </div>
  )
}

const INITIAL_DIFFER_OPTIONS = {
  lineWrapping: true,
  connect: null,
  highlightDifferences: true,
  ignoreWhitespace: false,
  collapseIdentical: false,
}

export const Differ = ({
  mode,
  textLeft,
  textMiddle,
  flex,
  collapseIdentical,
  menuPortal,
  bareMenuItems = false,
  lineNumbers,
  lineWrapping,
  onChange,
  headLeft,
  headMiddle,
  footerLeft,
  footerMiddle,
}) => {
  let theme = useSelector(root => getCodemirrorTheme(root.meta.theme.name))

  let [diffopts, setOpts] = usePersistentShallow(`differ`, INITIAL_DIFFER_OPTIONS, 1)

  let doOnChange = (editor, data, value) => {
    if (onChange) {
      onChange({editor, data, value})
    }
  }

  //~ let [orgTextMiddle, setOrgTextMiddle] = useState(null)
  //~ useEffect(() => {
  //~ if (!orgTextMiddle) {
  //~ setOrgTextMiddle(textMiddle)
  //~ }
  //~ }, [textMiddle])
  let orgTextMiddle = textMiddle

  let [editor, setEditor] = useState(null)
  let [leftEditor, setLeftEditor] = useState(null)

  let onEditorMount = (editor, leftEditor) => {
    //~ log.x('mount', editor)
    //~ setx(33)
    //~ log.x(editor)
    setEditor(editor)
    setLeftEditor(leftEditor)
  }

  let buildCaptions = (left, middle) => {
    if (left || middle) {
      return (
        <div style={{display: 'flex', backgroundColor: '#ddd'}}>
          <div style={{flex: 0.5, paddingLeft: '0.1em'}}>{left}</div>
          <div style={{flex: 0.5, paddingLeft: '2.1em'}}>{middle}</div>
        </div>
      )
    }
    return null
  }

  // hardlocked mode="sui"
  // use-case: DeltaReasonPortal (Target) and bareMenuItems
  let menu = (
    <>
      <Toolbar.Separator />
      <Toolbar.Group separator={true} position="right" title="Settings">
        <XMenu.Item
          mode="sui"
          active={diffopts.lineWrapping}
          onClick={() => setOpts({lineWrapping: !diffopts.lineWrapping})}
          icon="linewrap"
          title="Textwrap"
        />
        <XMenu.Item
          mode="sui"
          active={diffopts.connect === 'align'}
          onClick={() => setOpts({connect: !diffopts.connect ? 'align' : null})}
          icon="linkify"
          title="Connect"
        />
        <XMenu.Item
          mode="sui"
          active={diffopts.highlightDifferences}
          onClick={() => setOpts({highlightDifferences: !diffopts.highlightDifferences})}
          icon="lightbulb outline"
          title="Highlight"
        />
        <XMenu.Item
          mode="sui"
          active={diffopts.ignoreWhitespace}
          onClick={() => setOpts({ignoreWhitespace: !diffopts.ignoreWhitespace})}
          icon="ellipsis horizontal"
          title="IgnoreWhitespace"
        />
        <XMenu.Item
          mode="sui"
          onClick={() => editor.execCommand('goPrevDiff')}
          icon="arrow left"
          title="Prev"
        />
        <XMenu.Item
          mode="sui"
          onClick={() => editor.execCommand('goNextDiff')}
          icon="arrow right"
          title="Next"
        />
        {collapseIdentical === undefined && (
          <XMenu.Toggle
            mode="sui"
            value={diffopts.collapseIdentical}
            set={value => setOpts({collapseIdentical: value})}
            icon="compress"
            title="Collapse Identical"
          />
        )}
      </Toolbar.Group>
    </>
  )
  if (!bareMenuItems) {
    menu = <Toolbar>{menu}</Toolbar>
  }
  let readOnly = !Boolean(onChange)

  let cm = ({width, height}) => {
    if (height == 0) return null
    // XXX: hack - subtract header/footer height
    let delta_height = headLeft ? 20 : 0
    delta_height += footerLeft ? 20 : 0

    return (
      <DiffView
        width={width}
        height={height - delta_height}
        options={{
          value: orgTextMiddle || '',
          origLeft: textLeft || '',
          lineNumbers,
          lineWrapping: isNil(lineWrapping) ? diffopts.lineWrapping : lineWrapping,
          highlightDifferences: diffopts.highlightDifferences,
          ignoreWhitespace: diffopts.ignoreWhitespace,
          connect: diffopts.connect,
          mode,
          theme,
          collapseIdentical: collapseIdentical ?? diffopts.collapseIdentical,
          readOnly,
          revertButtons: !readOnly,
        }}
        editorDidMount={onEditorMount}
        onChange={doOnChange}
      />
    )
  }

  let result = <AutoSizer>{cm}</AutoSizer>

  if (flex) {
    result = <div style={{display: 'flex', flex: 1}}>{result}</div>
  }
  return (
    <>
      {buildCaptions(headLeft, headMiddle)}
      {result}
      {buildCaptions(footerLeft, footerMiddle)}
      {menuPortal && <AutoPortal target={menuPortal}>{menu}</AutoPortal>}
    </>
  )
}
